import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatDate } from '../../lib/dateUtils';
import { API_URL, auth, errMsg } from './financeApi';
import { PaymentRow } from './PaymentRow';
import { FileViewer } from './FileViewer';
import { ConfirmDialog } from './ConfirmDialog';

const PLAN_BADGE = { active: 'bg-emerald-600', expired: 'bg-red-600', none: 'bg-slate-400' };

const VehicleRow = ({ v, onSwitch }) => (
  <div className={`bg-white border rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-2 justify-between ${v.expiring_soon ? 'border-amber-300' : ''}`} data-testid={`it-fin-vehicle-${v.registration_number}`}>
    <div className="text-sm min-w-0">
      <p className="font-semibold">{v.registration_number} <span className="font-normal text-muted-foreground">{v.make} {v.model}</span></p>
      <p className="text-xs text-muted-foreground truncate">{v.operator_name}{v.driver_name ? ` · driver ${v.driver_name}` : ''}</p>
      {v.trials.map((t, i) => <p key={i} className="text-xs text-sky-700">Trial: {t.user_name} ({t.role}) · {t.days_left} day(s) left</p>)}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <Badge className={PLAN_BADGE[v.plan_status]} data-testid={`it-fin-plan-${v.registration_number}`}>{v.plan_status === 'active' ? 'Plan active' : v.plan_status === 'expired' ? 'Expired' : 'No plan'}</Badge>
      {v.plan_expires_at && <span className="text-xs">{v.plan_status === 'active' ? 'Expires' : 'Expired'} {formatDate(v.plan_expires_at)}{v.plan_status === 'active' ? ` (${v.days_left} days)` : ''}</span>}
      {v.expiring_soon && <span className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Expiring soon</span>}
      {v.pending_payments > 0 && <Badge className="bg-amber-500">Proof waiting</Badge>}
      <label className="flex items-center gap-2 text-xs ml-2">
        <Switch checked={!v.disabled} onCheckedChange={(on) => onSwitch(v, on)} data-testid={`it-fin-switch-${v.registration_number}`} />
        {v.disabled ? 'Off' : 'On'}
      </label>
    </div>
  </div>
);

const RejectDialog = ({ payment, onClose, onReject }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { setReason(''); }, [payment]);
  return (
    <Dialog open={!!payment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="it-fin-reject-dialog">
        <DialogHeader>
          <DialogTitle>Proof doesn't match?</DialogTitle>
          <DialogDescription>Tell {payment?.paid_by_name} why the proof for {payment?.registration_number} was not accepted.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. No money received with this reference" data-testid="it-fin-reject-reason" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700" disabled={!reason.trim()} onClick={() => onReject(payment, reason)} data-testid="it-fin-reject-submit">Not accepted</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ITFinance = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [trials, setTrials] = useState([]);
  const [onlyExpiring, setOnlyExpiring] = useState(false);
  const [viewFile, setViewFile] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [ending, setEnding] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, p, t] = await Promise.all([
        axios.get(`${API_URL}/it/finance/overview`, auth(token)),
        axios.get(`${API_URL}/it/finance/payments`, auth(token)),
        axios.get(`${API_URL}/it/finance/trials`, auth(token)),
      ]);
      setOverview(o.data); setPayments(p.data); setTrials(t.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not load Finance Tracker data'));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try {
      const res = await fn();
      toast.success(res.data.message || okMsg);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
    }
  };

  const confirm = (p) => act(() => axios.put(`${API_URL}/it/finance/payments/${p.payment_id}/confirm`, {}, auth(token)));
  const reject = (p, reason) => { setRejecting(null); act(() => axios.put(`${API_URL}/it/finance/payments/${p.payment_id}/reject`, { reason }, auth(token))); };
  const switchVehicle = (v, enabled) => act(() => axios.put(`${API_URL}/it/finance/vehicles/${v.vehicle_id}/switch`, { enabled }, auth(token)));
  const endTrial = () => { const t = ending; setEnding(null); act(() => axios.put(`${API_URL}/it/finance/trials/${t.trial_id}/end`, {}, auth(token))); };

  if (!overview) return null;
  const pending = payments.filter((p) => p.status === 'pending');
  const vehicles = overview.vehicles.filter((v) => !onlyExpiring || v.expiring_soon);
  const c = overview.counts;

  return (
    <Card data-testid="it-finance-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-700" /> Finance Tracker</CardTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 text-sm">
          <p className="rounded-xl bg-amber-50 p-3" data-testid="it-fin-count-pending"><strong className="text-xl block">{c.pending_payments}</strong>Proofs to check</p>
          <p className="rounded-xl bg-emerald-50 p-3" data-testid="it-fin-count-active"><strong className="text-xl block">{c.active}</strong>Active plans</p>
          <p className="rounded-xl bg-orange-50 p-3" data-testid="it-fin-count-expiring"><strong className="text-xl block">{c.expiring_soon}</strong>Expiring in 3 months</p>
          <p className="rounded-xl bg-sky-50 p-3" data-testid="it-fin-count-trials"><strong className="text-xl block">{c.active_trials}</strong>Active trials</p>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="check">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="check" data-testid="it-fin-tab-check">Proofs to check ({pending.length})</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="it-fin-tab-vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="trials" data-testid="it-fin-tab-trials">Trials</TabsTrigger>
            <TabsTrigger value="history" data-testid="it-fin-tab-history">All payments</TabsTrigger>
          </TabsList>
          <TabsContent value="check" className="space-y-2 mt-3">
            <p className="text-xs text-muted-foreground">Check each proof against the money received in the bank. The plan starts only after you confirm. EFT only — no cash.</p>
            {pending.length === 0 && <p className="text-sm text-muted-foreground" data-testid="it-fin-no-pending">Nothing waiting.</p>}
            {pending.map((p) => (
              <PaymentRow key={p.payment_id} p={p} onViewProof={(x) => setViewFile({ kind: 'payment', id: x.payment_id, title: `Proof · ${x.eft_reference}` })}>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => confirm(p)} data-testid={`it-fin-confirm-${p.payment_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm</Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setRejecting(p)} data-testid={`it-fin-reject-${p.payment_id}`}><XCircle className="w-4 h-4 mr-1" /> Not accepted</Button>
              </PaymentRow>
            ))}
          </TabsContent>
          <TabsContent value="vehicles" className="space-y-2 mt-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={onlyExpiring} onCheckedChange={setOnlyExpiring} data-testid="it-fin-only-expiring" /> Only show vehicles about to expire</label>
            {vehicles.length === 0 && <p className="text-sm text-muted-foreground">No vehicles.</p>}
            {vehicles.map((v) => <VehicleRow key={v.vehicle_id} v={v} onSwitch={switchVehicle} />)}
          </TabsContent>
          <TabsContent value="trials" className="space-y-2 mt-3">
            {trials.length === 0 && <p className="text-sm text-muted-foreground" data-testid="it-fin-no-trials">No trials yet.</p>}
            {trials.map((t) => (
              <div key={t.trial_id} className="bg-white border rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm" data-testid={`it-fin-trial-${t.trial_id}`}>
                <div>
                  <p className="font-semibold">{t.user_name} <span className="font-normal text-muted-foreground">({t.role === 'transport' ? 'Transport Operator' : 'Driver'}) · {t.registration_number}</span></p>
                  <p className="text-xs text-muted-foreground">{formatDate(t.started_at)} – {formatDate(t.ends_at)}{t.ended_early_at ? ` · ended early by ${t.ended_early_by}` : ''}</p>
                </div>
                {t.active
                  ? <div className="flex items-center gap-2"><Badge className="bg-sky-600">{t.days_left} day(s) left</Badge><Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setEnding(t)} data-testid={`it-fin-end-trial-${t.trial_id}`}>End trial early</Button></div>
                  : <Badge className="bg-slate-500">Ended</Badge>}
              </div>
            ))}
          </TabsContent>
          <TabsContent value="history" className="space-y-2 mt-3">
            {payments.length === 0 && <p className="text-sm text-muted-foreground">No payments yet.</p>}
            {payments.map((p) => <PaymentRow key={p.payment_id} p={p} onViewProof={(x) => setViewFile({ kind: 'payment', id: x.payment_id, title: `Proof · ${x.eft_reference}` })} />)}
          </TabsContent>
        </Tabs>
      </CardContent>
      <FileViewer file={viewFile} token={token} onClose={() => setViewFile(null)} />
      <RejectDialog payment={rejecting} onClose={() => setRejecting(null)} onReject={reject} />
      <ConfirmDialog open={!!ending} title="End this trial early?" description={`${ending?.user_name} will not be able to add new expenses. Their records stay.`} confirmLabel="Yes, end trial" onConfirm={endTrial} onCancel={() => setEnding(null)} testid="end-trial-confirm" />
    </Card>
  );
};
